import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, ScrollView, Pressable, Platform, TextInput, ActivityIndicator } from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import PublicFooter from '@/components/PublicFooter';
import { buildBlogIndexStructuredData } from '@/lib/blogStructuredData';
import FlowField from '@/components/Landing/FlowField';
import { blogApi } from '@/lib/blogApi';
import slugify from '@/constants/utils/slugify';
import { BlogPostCard } from '@/components/Blog/BlogPostCard';

// ============================================================================
// HELPERS
// ============================================================================
const getBlogCategorySlug = (cat) => {
  if (cat == null) return null;
  if (typeof cat === 'object' && cat.slug) return cat.slug;
  if (typeof cat === 'string') {
    const s = slugify(cat);
    return s || null;
  }
  return null;
};

const getBlogCategoryLabel = (cat) => {
  if (cat == null) return '';
  if (typeof cat === 'object' && cat.name) return cat.name;
  return String(cat);
};

// ============================================================================
// CATEGORY PILL
// ============================================================================
const CategoryPill = React.memo(({ label, isActive, onPress, isDark }) => (
  <Pressable
    onPress={onPress}
    style={({ hovered }) => ({
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 100,
      backgroundColor: isActive
        ? '#8B5CF6'
        : hovered
          ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')
          : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
      borderWidth: 1,
      borderColor: isActive
        ? '#8B5CF6'
        : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
      transition: 'all 0.2s ease',
    })}
  >
    <Text
      size="s"
      weight={isActive ? 'medium' : 'normal'}
      style={{ color: isActive ? '#FFFFFF' : undefined }}
      color={isActive ? undefined : 'secondary'}
    >
      {label}
    </Text>
  </Pressable>
));

CategoryPill.displayName = 'CategoryPill';

// ============================================================================
// MAIN BLOG INDEX
// ============================================================================
export default function BlogIndex() {
  const { colors, theme } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const router = useRouter();
  const pathname = usePathname();
  const isDark = isDarkTheme(theme);

  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, hasMore: false, total: 0, pages: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const searchDebounceRef = useRef(null);

  const isBlogIndex = pathname === '/blog' || pathname?.endsWith('/blog');

  const sectionPadding = isXS ? 20 : 40;

  // ── Initial Load ──
  useEffect(() => {
    const loadInitial = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [postsData, cats, featured] = await Promise.all([
          blogApi.listPosts({ page: 1 }),
          blogApi.getCategories(),
          blogApi.getFeaturedPosts(2),
        ]);
        setPosts(postsData.posts);
        setPagination(postsData.pagination);
        setCategories(cats);
        setFeaturedPosts(featured);
      } catch (err) {
        console.error('Blog load error:', err);
        setError(err.message || 'Failed to load blog posts');
      } finally {
        setIsLoading(false);
      }
    };
    loadInitial();
  }, []);

  // ── Fetch with filters ──
  const fetchPosts = useCallback(async ({ page = 1, query = searchQuery, append = false } = {}) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      let data;
      if (query?.trim()) {
        data = await blogApi.searchPosts({
          q: query.trim(),
          page,
        });
      } else {
        data = await blogApi.listPosts({ page });
      }
      if (append) {
        setPosts((prev) => [...prev, ...data.posts]);
      } else {
        setPosts(data.posts);
      }
      setPagination(data.pagination);
    } catch (err) {
      console.error('Blog fetch error:', err);
      setError(err.message || 'Failed to load blog posts');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [searchQuery]);

  // ── Search with debounce ──
  const handleSearchChange = useCallback((text) => {
    setSearchQuery(text);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchPosts({ page: 1, query: text });
    }, 400);
  }, [fetchPosts]);

  // ── Load more ──
  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || !pagination.hasMore) return;
    fetchPosts({ page: pagination.page + 1, append: true });
  }, [fetchPosts, isLoadingMore, pagination]);

  // ── Navigate to post ──
  const handlePostPress = useCallback((slug) => {
    router.push(`/blog/${slug}`);
  }, [router]);

  // ── Column count ──
  const columns = isXS ? 1 : isSM ? 2 : 3;
  const gap = isXS ? 16 : 20;

  // ── Inject scrollbar CSS ──
  useEffect(() => {
    if (Platform.OS === 'web') {
      const style = document.createElement('style');
      style.textContent = `
        .blog-scroll::-webkit-scrollbar { width: 8px; }
        .blog-scroll::-webkit-scrollbar-track { background: transparent; }
        .blog-scroll::-webkit-scrollbar-thumb { background: rgba(150,150,150,0.2); border-radius: 4px; }
        .blog-scroll::-webkit-scrollbar-thumb:hover { background: rgba(150,150,150,0.4); }
      `;
      document.head.appendChild(style);
      return () => { document.head.removeChild(style); };
    }
  }, []);

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="AI News, Guides and Tutorials"
        description="AI news and practical guides: model releases explained, prompt and workflow tutorials for images, video, music and code, plus privacy coverage. Free to read."
        canonical="/blog"
        keywords="AI news, AI guides, AI tutorials, AI blog, prompt tutorials, AI model releases, image generation guides, AI privacy"
        rssUrl="/json/blog/rss"
        structuredData={buildBlogIndexStructuredData()}
      />

      <ScrollView
        className="blog-scroll"
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ============================================================ */}
        {/* HERO */}
        {/* ============================================================ */}
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingHorizontal: sectionPadding,
            paddingTop: isXS ? 100 : 140,
            paddingBottom: isXS ? 32 : 48,
          }}
        >
          <Text
            size="s"
            weight="medium"
            style={{
              color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
              textTransform: 'uppercase',
              letterSpacing: 2,
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            Blog
          </Text>
          <Text
            weight="bold"
            style={{
              fontSize: isXS ? 36 : isSM ? 44 : 56,
              textAlign: 'center',
              marginBottom: 16,
              lineHeight: isXS ? 42 : isSM ? 52 : 64,
              letterSpacing: -1.5,
            }}
          >
            Stories & Insights
          </Text>
          <Text
            size="l"
            color="secondary"
            style={{
              textAlign: 'center',
              maxWidth: 560,
              lineHeight: isXS ? 26 : 30,
              fontSize: isXS ? 16 : 19,
              marginBottom: isXS ? 28 : 40,
            }}
          >
            The latest on AI, technology, privacy, and building the future — straight from the Gab AI team.
          </Text>

          {/* Search */}
          <View
            style={{
              width: '100%',
              maxWidth: 520,
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: 14,
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              paddingHorizontal: 16,
              height: isXS ? 48 : 52,
            }}
          >
            <Icon
              name="magnifying-glass"
              size={16}
              color={isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}
            />
            <TextInput
              value={searchQuery}
              onChangeText={handleSearchChange}
              placeholder="Search articles..."
              placeholderTextColor={isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}
              style={{
                flex: 1,
                marginLeft: 12,
                fontSize: 16,
                color: colors.text_color_primary,
                outlineStyle: 'none',
                ...(Platform.OS === 'web' && { outline: 'none' }),
              }}
              accessibilityLabel="Search blog posts"
            />
            {searchQuery.length > 0 && (
              <Pressable onPress={() => handleSearchChange('')} style={{ padding: 4 }}>
                <Icon name="xmark" size={14} color={colors.text_color_secondary} />
              </Pressable>
            )}
          </View>
        </View>

        {/* ============================================================ */}
        {/* CATEGORY FILTER */}
        {/* ============================================================ */}
        {categories.length > 0 && !searchQuery && (
          <View
            style={{
              width: '100%',
              maxWidth: 1100,
              paddingHorizontal: sectionPadding,
              marginBottom: isXS ? 24 : 32,
            }}
          >
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 8, paddingVertical: 4 }}
            >
              <CategoryPill
                label="All"
                isActive={isBlogIndex}
                onPress={() => router.push('/blog')}
                isDark={isDark}
              />
              {categories.map((cat) => (
                <CategoryPill
                  key={getBlogCategorySlug(cat) || getBlogCategoryLabel(cat)}
                  label={getBlogCategoryLabel(cat)}
                  isActive={pathname === `/blog/category/${getBlogCategorySlug(cat)}`}
                  onPress={() => {
                    const s = getBlogCategorySlug(cat);
                    if (s) router.push(`/blog/category/${s}`);
                  }}
                  isDark={isDark}
                />
              ))}
            </ScrollView>
          </View>
        )}

        {/* ============================================================ */}
        {/* FEATURED POSTS */}
        {/* ============================================================ */}
        {!isLoading && !searchQuery && featuredPosts.length > 0 && (
          <View
            style={{
              width: '100%',
              maxWidth: 1100,
              paddingHorizontal: sectionPadding,
              marginBottom: isXS ? 32 : 48,
            }}
          >
            <Text
              weight="semibold"
              style={{
                fontSize: isXS ? 20 : 24,
                letterSpacing: -0.5,
                marginBottom: isXS ? 16 : 20,
              }}
            >
              Featured
            </Text>
            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                gap,
              }}
            >
              {/* Large featured card */}
              <View style={{ flex: isXS ? undefined : 1 }}>
                <BlogPostCard
                  post={featuredPosts[0]}
                  isDark={isDark}
                  isXS={isXS}
                  isSM={isSM}
                  onPress={handlePostPress}
                  isFeatured
                />
              </View>

              {/* Smaller featured cards */}
              {featuredPosts.length > 1 && (
                <View style={{ flex: isXS ? undefined : 1 }}>
                  {featuredPosts.slice(1, 2).map((post) => (
                    <BlogPostCard
                      key={post.id || post.slug}
                      post={post}
                      isDark={isDark}
                      isXS={isXS}
                      isSM={isSM}
                      onPress={handlePostPress}
                    />
                  ))}
                </View>
              )}
            </View>
          </View>
        )}

        {/* ============================================================ */}
        {/* POST GRID */}
        {/* ============================================================ */}
        <View
          style={{
            width: '100%',
            maxWidth: 1100,
            paddingHorizontal: sectionPadding,
            paddingBottom: isXS ? 40 : 60,
          }}
        >
          {searchQuery && !isLoading && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <Text size="m" color="secondary">
                {`Results for "${searchQuery}"`}
              </Text>
              <Text size="s" color="tertiary">
                ({pagination.total || 0} {pagination.total === 1 ? 'article' : 'articles'})
              </Text>
            </View>
          )}

          {/* Loading state */}
          {isLoading && (
            <View style={{ alignItems: 'center', paddingVertical: 80 }}>
              <ActivityIndicator size="large" color="#8B5CF6" />
              <Text size="s" color="tertiary" style={{ marginTop: 16 }}>
                Loading articles...
              </Text>
            </View>
          )}

          {/* Error state */}
          {!isLoading && error && (
            <View
              style={{
                alignItems: 'center',
                paddingVertical: 60,
                paddingHorizontal: 20,
                borderRadius: 20,
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                borderWidth: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              }}
            >
              <Icon name="circle-exclamation" size={32} color={isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} />
              <Text size="m" color="secondary" style={{ marginTop: 16, textAlign: 'center' }}>
                {error}
              </Text>
              <Button
                text="Try Again"
                variant="secondary"
                size="small"
                isRound
                onPress={() => fetchPosts({ page: 1 })}
                style={{ marginTop: 16 }}
              />
            </View>
          )}

          {/* Empty state */}
          {!isLoading && !error && posts.length === 0 && (
            <View
              style={{
                alignItems: 'center',
                paddingVertical: 60,
                paddingHorizontal: 20,
                borderRadius: 20,
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                borderWidth: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              }}
            >
              <Icon name="newspaper" size={32} color={isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} />
              <Text size="m" weight="medium" style={{ marginTop: 16 }}>
                {searchQuery ? 'No articles found' : 'No posts yet'}
              </Text>
              <Text size="s" color="secondary" style={{ marginTop: 8, textAlign: 'center' }}>
                {searchQuery
                  ? 'Try adjusting your search or browse all articles.'
                  : 'Check back soon for new content.'}
              </Text>
              {searchQuery && (
                <Button
                  text="Clear Search"
                  variant="secondary"
                  size="small"
                  isRound
                  onPress={() => handleSearchChange('')}
                  style={{ marginTop: 16 }}
                />
              )}
            </View>
          )}

          {/* Post grid */}
          {!isLoading && !error && posts.length > 0 && (
            <>
              {!searchQuery && featuredPosts.length > 0 && (
                <Text
                  weight="semibold"
                  style={{
                    fontSize: isXS ? 20 : 24,
                    letterSpacing: -0.5,
                    marginBottom: isXS ? 16 : 20,
                  }}
                >
                  Latest Articles
                </Text>
              )}

              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap,
                }}
              >
                {posts.map((post) => {
                  const gapDeduction = columns > 1
                    ? (gap * (columns - 1)) / columns
                    : 0;
                  const columnPercent = 100 / columns;

                  return (
                    <View
                      key={post.id || post.slug}
                      style={{
                        width: columns === 1
                          ? '100%'
                          : `calc(${columnPercent}% - ${gapDeduction}px)`,
                        flexGrow: 0,
                        flexShrink: 0,
                      }}
                    >
                      <BlogPostCard
                        post={post}
                        isDark={isDark}
                        isXS={isXS}
                        isSM={isSM}
                        onPress={handlePostPress}
                      />
                    </View>
                  );
                })}
              </View>

              {/* Load more */}
              {pagination.hasMore && (
                <View style={{ alignItems: 'center', marginTop: isXS ? 32 : 48 }}>
                  <Button
                    text={isLoadingMore ? 'Loading...' : 'Load More Articles'}
                    variant="secondary"
                    size="medium"
                    isRound
                    onPress={handleLoadMore}
                    disabled={isLoadingMore}
                    style={{
                      paddingHorizontal: 32,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                    }}
                  />
                  <Text size="xs" color="tertiary" style={{ marginTop: 12 }}>
                    Showing {posts.length} of {pagination.total} articles
                  </Text>
                </View>
              )}
            </>
          )}
        </View>

        {/* ============================================================ */}
        {/* CTA */}
        {/* ============================================================ */}
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingHorizontal: sectionPadding,
            paddingVertical: isXS ? 48 : 80,
          }}
        >
          <View
            style={{
              maxWidth: 600,
              width: '100%',
              padding: isXS ? 32 : 48,
              borderRadius: 24,
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              alignItems: 'center',
            }}
          >
            <Text
              weight="bold"
              style={{
                fontSize: isXS ? 24 : 32,
                textAlign: 'center',
                marginBottom: 12,
                letterSpacing: -0.8,
              }}
            >
              Try Gab AI Today
            </Text>
            <Text
              color="secondary"
              style={{
                textAlign: 'center',
                marginBottom: 28,
                maxWidth: 400,
                lineHeight: 24,
                fontSize: isXS ? 15 : 17,
              }}
            >
              One AI for text, images, videos, music, voice, and more. Start creating for free.
            </Text>
            <Button
              text="Get Started Free"
              variant="purple"
              size="large"
              isRound
              onPress={() => router.push('/signup')}
              style={{ paddingHorizontal: isXS ? 32 : 48 }}
            />
          </View>
        </View>

        <PublicFooter />
      </ScrollView>
    </View>
  );
}
